import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  useCallback,
  HTMLAttributes,
  ComponentProps,
} from "react";
import { format, isAfter, isBefore } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/Button";
import { Calendar, CalendarDayButton } from "@/components/ui/Calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { Separator } from "@/components/ui/Separator";
import { cn } from "@/lib/utils";
import { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { VariantProps } from "class-variance-authority";

interface CustomDayButtonProps
  extends ComponentProps<typeof CalendarDayButton> {
  className?: string;
}

export interface DatePickerWithRangeProps
  extends HTMLAttributes<HTMLDivElement> {
  date: DateRange | undefined;
  setDate: (range: DateRange | undefined) => void;
  variant?: VariantProps<typeof buttonVariants>["variant"];
  placeholder?: string;
  disabled?: boolean;
  maxDate?: Date;
  minDate?: Date;
  onApply?: (range: DateRange | undefined) => void;
  showApplyButton?: boolean;
  preventAutoClose?: boolean;
  buttonClassName?: string;
}

export const DatePickerWithRange = forwardRef<
  { openPicker: () => void },
  DatePickerWithRangeProps
>(
  (
    {
      className,
      date,
      setDate,
      variant = "outline",
      placeholder = "Select date range",
      disabled = false,
      maxDate = new Date(), // Default to today
      minDate,
      onApply,
      showApplyButton = true,
      preventAutoClose = false,
      buttonClassName,
      ...props
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [tempRange, setTempRange] = useState<DateRange | undefined>(date);
    const { t } = useTranslation();

    // Expose openPicker method to parent
    useImperativeHandle(ref, () => ({
      openPicker: () => {
        setIsOpen(true);
      },
    }));

    // Update temp range when prop changes
    useEffect(() => {
      setTempRange(date);
    }, [date]);

    // Format display text
    const formatDateRange = useCallback(() => {
      if (!date?.from) return placeholder;

      if (!date.to) {
        return format(date.from, "LLL dd");
      }

      // If same month and year, show abbreviated format
      const sameMonth =
        date.from.getMonth() === date.to.getMonth() &&
        date.from.getFullYear() === date.to.getFullYear();

      if (sameMonth) {
        return `${format(date.from, "LLL dd")} - ${format(date.to, "dd")}`;
      }

      // If different months but same year
      const sameYear = date.from.getFullYear() === date.to.getFullYear();
      if (sameYear) {
        return `${format(date.from, "LLL dd")} - ${format(date.to, "LLL dd")}`;
      }

      // Different years
      return `${format(date.from, "LLL dd")} - ${format(date.to, "LLL dd")}`;
    }, [date, placeholder]);

    // Handle date selection
    const handleDateSelect = useCallback(
      (range: DateRange | undefined) => {
        setTempRange(range);

        // If not using apply button, update immediately
        if (!showApplyButton && range) {
          setDate(range);
          if (range.from && range.to) {
            setIsOpen(false);
          }
        }
      },
      [showApplyButton, setDate],
    );

    // Handle apply button click
    const handleApply = useCallback(() => {
      setDate(tempRange);
      onApply?.(tempRange);
      setIsOpen(false);
    }, [tempRange, setDate, onApply]);

    // Memoize disabled date function
    const isDateDisabled = useCallback(
      (day: Date) => {
        if (maxDate && isAfter(day, maxDate)) return true;
        if (minDate && isBefore(day, minDate)) return true;
        return false;
      },
      [maxDate, minDate],
    );

    return (
      <div className={cn("grid gap-2", className)} {...props}>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              id="date"
              variant={variant}
              disabled={disabled}
              className={cn(
                "justify-start text-left font-normal",
                !date?.from && "text-muted-foreground",
                buttonClassName,
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {formatDateRange()}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-fit p-0"
            align="start"
            onFocusOutside={(e) => {
              if (preventAutoClose) {
                e.preventDefault();
              }
            }}
          >
            <Calendar
              mode="range"
              selected={tempRange}
              onSelect={handleDateSelect}
              disabled={isDateDisabled}
              className="border-0"
              classNames={{
                range_start:
                  "bg-[var(--color-date-picker-range-background)] rounded-l-2xl",
                range_middle: "bg-[var(--color-date-picker-range-background)]",
                range_end:
                  "bg-[var(--color-date-picker-range-background)] rounded-r-2xl",
                day: "h-9 w-9 flex items-center justify-center",
                today: "rounded-none",
              }}
              components={{
                DayButton: ({ className, ...props }: CustomDayButtonProps) => (
                  <CalendarDayButton
                    {...props}
                    className={cn(
                      "h-8 w-8 rounded-full transition-transform hover:scale-105 focus:bg-accent focus:text-accent-foreground data-[range-end=true]:rounded-full data-[range-end=true]:border-3 data-[range-end=true]:border-primary data-[range-end=true]:bg-[var(--color-date-picker-range-background)] data-[range-end=true]:text-foreground data-[range-middle=true]:rounded-full data-[range-middle=true]:border-none data-[range-middle=true]:bg-[var(--color-date-picker-range-background)] data-[range-middle=true]:text-foreground data-[range-start=true]:rounded-full data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[selected-single=true]:rounded-full data-[selected-single=true]:border-3 data-[selected-single=true]:border-primary data-[selected-single=true]:text-foreground",
                      className,
                    )}
                  />
                ),
              }}
            />

            {showApplyButton && (
              <>
                <Separator className="my-2" />
                <div className="flex justify-end gap-2 p-2">
                  <Button
                    size="sm"
                    className="px-4 py-2"
                    onClick={handleApply}
                    disabled={!tempRange?.from}
                    variant="secondary"
                  >
                    {t("global.apply", "Apply")}
                  </Button>
                </div>
              </>
            )}
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);

DatePickerWithRange.displayName = "DatePickerWithRange";
